import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  ArrowLeft,
  Save,
  Car,
  Users,
  Package,
  DollarSign,
  Clock,
  AlertTriangle,
  Loader2,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  useVehicleCategory,
  useCreateVehicleCategory,
  useUpdateVehicleCategory,
  useAllVehicleCategories,
} from "@/api-services/hooks/useVehicleCategory";
import {
  VehicleCategoryDTO,
} from "@/api-services/hooks/vehicleCategoryTypes";
import { getStoredCurrency } from "@/api-services/hooks/useSettings";

const createVehicleCategoryFormSchema = (existingCategories: any[] | null | undefined, currentCategoryId?: number) => z.object({
  vehicleCategoryName: z.string()
    .min(1, "Vehicle category name is required")
    .max(100, "Name must be less than 100 characters")
    .refine((name) => {
      if (!name || name.trim() === '') return false;

      const isDuplicate = existingCategories && Array.isArray(existingCategories) && existingCategories.some((category: any) => {
        const categoryId = category.vehicleCategoryId || category.id;
        return category.vehicleCategoryName?.toLowerCase().trim() === name.toLowerCase().trim() &&
          categoryId !== currentCategoryId;
      });

      return !isDuplicate;
    }, {
      message: "Vehicle category name already exists"
    }),
  vehicleDescription: z.string().min(1, "Description is required").max(500, "Description must be less than 500 characters"),
  maxPassengerCount: z.number().min(1, "Max passenger count must be at least 1").max(20, "Max passenger count cannot exceed 20"),
  maxLuggageVolume: z.number().min(0, "Max luggage volume must be at least 0").max(1000, "Max luggage volume cannot exceed 1000 liters"),
  baseFare: z.number().min(0, "Base fare must be at least 0"),
  ratePerKm: z.number().min(0, "Rate per km must be at least 0"),
  ratePerMinute: z.number().min(0, "Rate per minute must be at least 0"),
  minimumFare: z.number().min(0, "Minimum fare must be at least 0").optional(),
  nightCharges: z.number().min(0, "Night charges must be at least 0").optional(),
  tollFeeApplicable: z.boolean().optional(),
  cancellationFee: z.number().min(0, "Cancellation fee must be at least 0"),
  // waitTimeLimit: z.number().min(1, "Wait time limit must be at least 1 minute").max(60, "Wait time limit cannot exceed 60 minutes"),
  vehicleIcon: z.string().max(255, "Icon URL must be less than 255 characters").optional(),
  isActive: z.boolean(),
});

type VehicleCategoryFormData = z.infer<ReturnType<typeof createVehicleCategoryFormSchema>>;

const AddEditVehicleCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const isEditing = !!categoryId;
  const categoryIdNum = categoryId ? parseInt(categoryId, 10) : 0;

  // Fetch all vehicle categories for validation
  const { data: allCategoriesData } = useAllVehicleCategories({
    onError: (error) => {
      // Silently handle error
    },
  });

  // Memoize categories array to prevent unnecessary re-renders
  // Create a stable reference by using a serialized key
  const categoriesDataString = useMemo(() => {
    if (!Array.isArray(allCategoriesData) || allCategoriesData.length === 0) return '';
    return JSON.stringify(allCategoriesData.map(c => ({
      id: c.vehicleCategoryId || c.id,
      name: c.vehicleCategoryName
    })));
  }, [allCategoriesData]);

  const allCategories = useMemo(() => {
    if (!Array.isArray(allCategoriesData)) return [];
    return allCategoriesData;
  }, [categoriesDataString]);

  // Fetch vehicle category data for edit
  const { data: categoryData, isLoading: loadingCategory, error: categoryError } = useVehicleCategory(
    categoryIdNum,
    {
      enabled: isEditing && categoryIdNum > 0,
      onError: (error) => {
        toast.error(`Failed to load vehicle category: ${error.message}`);
      },
      retry: 2,
      retryDelay: 1000,
    }
  );

  // Create/Update vehicle category hooks
  const { mutate: createVehicleCategory, isPending: isCreating } = useCreateVehicleCategory({
    onSuccess: (data, variables) => {
      toast.success("Vehicle category created successfully!");
      navigate("/admin/vehicle-categories");
    },
    onError: (error, variables) => {
      toast.error(`Failed to create vehicle category: ${error.message}`);
    },
  });

  const { mutate: updateVehicleCategory, isPending: isUpdating } = useUpdateVehicleCategory({
    onSuccess: (data, variables) => {
      toast.success("Vehicle category updated successfully!");
      navigate("/admin/vehicle-categories");
    },
    onError: (error, variables) => {
      toast.error(`Failed to update vehicle category: ${error.message}`);
    },
  });

  // Memoize the form schema to prevent recreation on every render
  // Use categoriesDataString as the dependency to ensure stable reference
  const formSchema = useMemo(() => {
    return createVehicleCategoryFormSchema(allCategories, categoryIdNum);
  }, [categoriesDataString, categoryIdNum]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid, touchedFields },
    reset,
    trigger,
  } = useForm<VehicleCategoryFormData>({
    resolver: zodResolver(formSchema),
    mode: "onBlur", // Only validate on blur, not on mount
    defaultValues: {
      vehicleCategoryName: "",
      vehicleDescription: "",
      maxPassengerCount: 4,
      maxLuggageVolume: 100,
      baseFare: 5.00,
      ratePerKm: 1.50,
      ratePerMinute: 0.30,
      minimumFare: 0,
      nightCharges: 0,
      tollFeeApplicable: false,
      cancellationFee: 2.00,
      // waitTimeLimit: 5,
      vehicleIcon: "",
      isActive: true,
    },
  });

  // Revalidate form when categories data changes (only if field has been touched)
  // Use categoriesDataString to avoid unnecessary triggers
  const shouldValidate = useMemo(() => {
    return allCategories.length > 0 && touchedFields.vehicleCategoryName;
  }, [allCategories.length, touchedFields.vehicleCategoryName]);

  useEffect(() => {
    if (shouldValidate && categoriesDataString) {
      trigger("vehicleCategoryName");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesDataString, shouldValidate]);

  // Load data when editing - use a ref to track if we've already loaded
  const hasLoadedData = React.useRef(false);
  const lastCategoryId = React.useRef<number | null>(null);

  // Reset flag when categoryId changes - do this in useEffect, not during render
  useEffect(() => {
    if (lastCategoryId.current !== categoryIdNum) {
      lastCategoryId.current = categoryIdNum;
      hasLoadedData.current = false;
    }
  }, [categoryIdNum]);

  // Load data when editing - only run once per category
  useEffect(() => {
    if (
      categoryData && 
      isEditing && 
      !hasLoadedData.current && 
      categoryData.vehicleCategoryId &&
      categoryData.vehicleCategoryId === categoryIdNum
    ) {
      hasLoadedData.current = true;
      reset({
        vehicleCategoryName: categoryData.vehicleCategoryName || "",
        vehicleDescription: categoryData.vehicleDescription || "",
        maxPassengerCount: categoryData.maxPassengerCount || 4,
        maxLuggageVolume: categoryData.maxLuggageVolume || 100,
        baseFare: categoryData.baseFare || 5.00,
        ratePerKm: categoryData.ratePerKm || 1.50,
        ratePerMinute: categoryData.ratePerMinute || 0.30,
        minimumFare: categoryData.minimumFare || 0,
        nightCharges: categoryData.nightCharges || 0,
        tollFeeApplicable: categoryData.tollFeeApplicable ?? (categoryData as any).isTollFeeApplicable ?? false,
        cancellationFee: categoryData.cancellationFee || 2.00,
        // waitTimeLimit: categoryData.waitTimeLimit || 5,
        vehicleIcon: categoryData.vehicleIcon || "",
        isActive: categoryData.isActive ?? true,
      });
    }
    // Only depend on the specific category ID, not the whole categoryData object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData?.vehicleCategoryId, categoryIdNum, isEditing]);

  const handleCategoryNameBlur = () => {
    trigger("vehicleCategoryName");
  };

  const onSubmit = (data: VehicleCategoryFormData) => {
    const payload = {
      ...data,
      isTollFeeApplicable: data.tollFeeApplicable ?? false,
    } as VehicleCategoryDTO;

    if (isEditing) {
      updateVehicleCategory({
        id: categoryIdNum,
        data: payload,
      });
    } else {
      createVehicleCategory(payload);
    }
  };

  const isLoading = loadingCategory || isCreating || isUpdating;
  const isActive = watch("isActive");

  // Show error if vehicle category not found
  if (isEditing && !loadingCategory && !categoryData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Vehicle Category Not Found
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-red-700">The vehicle category you're trying to edit could not be found.</p>
            <Button
              onClick={() => navigate("/admin/vehicle-categories")}
              className="w-full"
            >
              Back to Vehicle Categories
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadingCategory && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading vehicle category...</p>
        </div>
      </div>
    );
  }

  if (categoryError && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Failed to Load Vehicle Category
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-red-700">{categoryError.message}</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="flex-1"
              >
                Retry
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/admin/vehicle-categories")}
                className="flex-1"
              >
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/vehicle-categories")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {isEditing ? "Edit Vehicle Category" : "Add New Vehicle Category"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditing
                ? "Update vehicle category details and pricing"
                : "Create a new vehicle category with pricing and capacity settings"
              }
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => toast.error('Please fix the validation errors'))} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5" />
                Basic Information
              </CardTitle>
              <CardDescription>
                General details about the vehicle category
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vehicleCategoryName">Category Name <span className="text-red-500">*</span></Label>
                <Input
                  id="vehicleCategoryName"
                  placeholder="e.g., Economy, Premium, SUV"
                  {...register("vehicleCategoryName")}
                  onBlur={handleCategoryNameBlur}
                  className={errors.vehicleCategoryName ? "border-red-500" : ""}
                />
                {errors.vehicleCategoryName && (
                  <p className="text-sm text-red-500">{errors.vehicleCategoryName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleDescription">Description <span className="text-red-500">*</span></Label>
                <Textarea
                  id="vehicleDescription"
                  placeholder="Brief description of the vehicle category"
                  rows={3}
                  {...register("vehicleDescription")}
                />
                {errors.vehicleDescription && (
                  <p className="text-sm text-red-500">{errors.vehicleDescription.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleIcon">Vehicle Icon URL</Label>
                <Input
                  id="vehicleIcon"
                  placeholder="https://example.com/icon.png"
                  {...register("vehicleIcon")}
                />
                {errors.vehicleIcon && (
                  <p className="text-sm text-red-500">{errors.vehicleIcon.message}</p>
                )}
                {watch("vehicleIcon") && (
                  <div className="flex items-center gap-3 mt-2 p-3 border rounded-lg bg-muted/30">
                    <img
                      src={watch("vehicleIcon")}
                      alt="Vehicle icon preview"
                      className="h-14 w-14 object-contain rounded border bg-white p-1"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                      onLoad={(e) => {
                        e.currentTarget.style.display = 'block';
                        e.currentTarget.nextElementSibling?.classList.add('hidden');
                      }}
                    />
                    <div className="hidden flex items-center gap-2 text-sm text-red-500">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Failed to load image from URL</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={(checked) => setValue("isActive", checked as boolean)}
                />
                <Label htmlFor="isActive">Category is active</Label>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Capacity & Luggage
              </CardTitle>
              <CardDescription>
                Passenger and luggage capacity settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="maxPassengerCount">Max Passengers <span className="text-red-500">*</span></Label>
                <Input
                  id="maxPassengerCount"
                  type="number"
                  min="1"
                  max="20"
                  {...register("maxPassengerCount", { valueAsNumber: true })}
                  className={errors.maxPassengerCount ? "border-red-500" : ""}
                />
                {errors.maxPassengerCount && (
                  <p className="text-sm text-red-500">{errors.maxPassengerCount.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxLuggageVolume">Max Luggage Volume (Liters) <span className="text-red-500">*</span></Label>
                <Input
                  id="maxLuggageVolume"
                  type="number"
                  min="0"
                  max="1000"
                  step="0.1"
                  {...register("maxLuggageVolume", { valueAsNumber: true })}
                  className={errors.maxLuggageVolume ? "border-red-500" : ""}
                />
                {errors.maxLuggageVolume && (
                  <p className="text-sm text-red-500">{errors.maxLuggageVolume.message}</p>
                )}
              </div>

              {/* <div className="space-y-2">
                <Label htmlFor="waitTimeLimit">Wait Time Limit (Minutes) <span className="text-red-500">*</span></Label>
                <Input
                  id="waitTimeLimit"
                  type="number"
                  min="1"
                  max="60"
                  {...register("waitTimeLimit", { valueAsNumber: true })}
                  className={errors.waitTimeLimit ? "border-red-500" : ""}
                />
                {errors.waitTimeLimit && (
                  <p className="text-sm text-red-500">{errors.waitTimeLimit.message}</p>
                )}
              </div> */}

            </CardContent>
          </Card>

          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {/* <DollarSign className="w-5 h-5" /> */}
                {getStoredCurrency()} {" "}
                Pricing Settings
              </CardTitle>
              <CardDescription>
                Default pricing for this vehicle category
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="baseFare">Base Fare ({getStoredCurrency()}) <span className="text-red-500">*</span></Label>
                  <Input
                    id="baseFare"
                    type="number"
                    min="0"
                    step="0.01"
                    {...register("baseFare", { valueAsNumber: true })}
                    className={errors.baseFare ? "border-red-500" : ""}
                  />
                  {errors.baseFare && (
                    <p className="text-sm text-red-500">{errors.baseFare.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ratePerKm">Rate per Kilometer ({getStoredCurrency()}) <span className="text-red-500">*</span></Label>
                  <Input
                    id="ratePerKm"
                    type="number"
                    min="0"
                    step="0.01"
                    {...register("ratePerKm", { valueAsNumber: true })}
                    className={errors.ratePerKm ? "border-red-500" : ""}
                  />
                  {errors.ratePerKm && (
                    <p className="text-sm text-red-500">{errors.ratePerKm.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ratePerMinute">Rate per Minute ({getStoredCurrency()}) <span className="text-red-500">*</span></Label>
                  <Input
                    id="ratePerMinute"
                    type="number"
                    min="0"
                    step="0.01"
                    {...register("ratePerMinute", { valueAsNumber: true })}
                    className={errors.ratePerMinute ? "border-red-500" : ""}
                  />
                  {errors.ratePerMinute && (
                    <p className="text-sm text-red-500">{errors.ratePerMinute.message}</p>
                  )}
                </div>

                {/* <div className="space-y-2">
                  <Label htmlFor="minimumFare">Minimum Fare ({getStoredCurrency()})</Label>
                  <Input
                    id="minimumFare"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    {...register("minimumFare", { valueAsNumber: true })}
                    className={errors.minimumFare ? "border-red-500" : ""}
                  />
                  {errors.minimumFare && (
                    <p className="text-sm text-red-500">{errors.minimumFare.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">Minimum fare for any trip (0 = no minimum)</p>
                </div> */}

                <div className="space-y-2">
                  <Label htmlFor="nightCharges">Night Charges ({getStoredCurrency()})</Label>
                  <Input
                    id="nightCharges"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    {...register("nightCharges", { valueAsNumber: true })}
                    className={errors.nightCharges ? "border-red-500" : ""}
                  />
                  {errors.nightCharges && (
                    <p className="text-sm text-red-500">{errors.nightCharges.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">Additional charges for night rides (0 = no night charge)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tollFeeApplicable">Toll Fee Settings</Label>
                  <div className="flex items-center space-x-2 mt-4">
                    <Checkbox
                      id="tollFeeApplicable"
                      checked={watch("tollFeeApplicable") || false}
                      onCheckedChange={(checked) => setValue("tollFeeApplicable", checked as boolean)}
                    />
                    <Label
                      htmlFor="tollFeeApplicable"
                      className="text-sm font-normal"
                    >
                      Toll fees are applicable for this vehicle type
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Fees */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Additional Fees
              </CardTitle>
              <CardDescription>
                Cancellation and other additional fees
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cancellationFee">Cancellation Fee ({getStoredCurrency()}) <span className="text-red-500">*</span></Label>
                <Input
                  id="cancellationFee"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("cancellationFee", { valueAsNumber: true })}
                  className={errors.cancellationFee ? "border-red-500" : ""}
                />
                {errors.cancellationFee && (
                  <p className="text-sm text-red-500">{errors.cancellationFee.message}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/vehicle-categories")}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-gradient-primary hover:opacity-90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Update Category" : "Create Category"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditVehicleCategoryPage;